/**
 * Server-Sent Events (SSE) istemcisi — Artemis'teki src/utils/sseClient.js'in Apollo karşılığı.
 *
 * axios stream yanıtlarını satır satır okuyamadığı için (XHR tüm gövdeyi biriktirir)
 * stream uçları fetch + ReadableStream ile tüketilir. src/utils/axios.js içindeki
 * interceptor'ın yaptığı işi (baseURL, auth header'ları, oturum düşmesi) burada tekrarlar.
 *
 * Kullanım:
 *   await postEventStream('/ChatWithProductStream', body, {
 *     signal: controller.signal,
 *     onEvent: ({ event, data }) => { ... }
 *   });
 */

import { getToken, getTokenWs, getIpAddress, getUser, handleLogout } from 'src/utils/Common';

/** HTTP seviyesinde (200 dışı) biten stream istekleri için hata tipi. */
export class SseHttpError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'SseHttpError';
    this.status = status;
    this.body = body;
    // Oturum düşmesi gibi durumlarda uyarı bu modülde verildiği için çağıran taraf
    // bu bayrağı görüp ikinci bir hata mesajı basmaz.
    this.handled = false;
  }
}

/** İsteğin iptal edilip edilmediğini (AbortController) anlamak için. */
export const isAbortError = (error) =>
  !!error && (error.name === 'AbortError' || error.code === 20);

const buildUrl = (path) => {
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  return `${base}/${String(path || '').replace(/^\/+/, '')}`;
};

const buildHeaders = (extraHeaders) => {
  const user = getUser();
  const authToken = getToken();

  return {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    Authorization: 'Bearer ' + authToken,
    SessionToken: authToken,
    SessionTokenWs: getTokenWs(),
    IpAddress: getIpAddress() || '',
    'Auth-UserId': user ? user.UserId : '',
    ...extraHeaders
  };
};

/**
 * SSE çerçeve ayrıştırıcı (https://html.spec.whatwg.org/multipage/server-sent-events.html)
 *
 * - Alan değerinden yalnızca TEK baştaki boşluk atılır. Backend parçaları "data: {metin}"
 *   olarak yazıyor ve metnin kendisi de boşlukla başlayabiliyor (kelime araları); fazlası
 *   atılırsa kelimeler bitişik görünür.
 * - Birden fazla "data:" satırı "\n" ile birleştirilir.
 * - ":" ile başlayan satırlar (keep-alive) yok sayılır.
 * - Bağlantı koptuğunda yarım kalan son çerçeve atılır; yarım JSON "final" gibi işlenmez.
 */
const createSseParser = (onEvent) => {
  let buffer = '';
  let eventName = '';
  let dataLines = [];
  let hasData = false;

  const dispatch = () => {
    if (!hasData) {
      eventName = '';
      return;
    }
    const event = eventName || 'message';
    const data = dataLines.join('\n');
    eventName = '';
    dataLines = [];
    hasData = false;
    onEvent({ event, data });
  };

  const handleLine = (rawLine) => {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;

    if (line === '') {
      dispatch();
      return;
    }
    if (line.charAt(0) === ':') {
      return;
    }

    const colonIndex = line.indexOf(':');
    const field = colonIndex === -1 ? line : line.slice(0, colonIndex);
    let value = colonIndex === -1 ? '' : line.slice(colonIndex + 1);
    if (value.charAt(0) === ' ') {
      value = value.slice(1);
    }

    if (field === 'event') {
      eventName = value;
    } else if (field === 'data') {
      dataLines.push(value);
      hasData = true;
    }
  };

  return {
    push(text) {
      buffer += text;
      const parts = buffer.split('\n');
      buffer = parts.pop();
      parts.forEach(handleLine);
    },
    close() {
      if (buffer) {
        const rest = buffer;
        buffer = '';
        handleLine(rest);
      }
    }
  };
};

/**
 * POST ile SSE uç noktasına bağlanır ve gelen her event için onEvent çağırır.
 * Stream tamamlandığında resolve olur.
 */
export async function postEventStream(path, body, { signal, onEvent, headers } = {}) {
  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers: buildHeaders(headers),
    body: JSON.stringify(body),
    signal,
    cache: 'no-store'
  });

  if (!response.ok) {
    let errorBody = '';
    try {
      errorBody = await response.text();
    } catch (e) {
      errorBody = '';
    }

    const error = new SseHttpError(`HTTP ${response.status}`, response.status, errorBody);

    // axios interceptor'ındaki oturum davranışının stream tarafındaki karşılığı.
    if (response.status === 401 || errorBody.includes('401 Unauthorized')) {
      error.handled = true;
      alert('Başka bir cihazdan giriş yaptığınız için oturumunuz sonlandırıldı!..');
      handleLogout();
    }

    throw error;
  }

  const parser = createSseParser(onEvent);

  // response.body olmayan tarayıcılarda yanıt tek parça olarak işlenir.
  if (!response.body || typeof response.body.getReader !== 'function') {
    parser.push(await response.text());
    parser.close();
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.close();
  } finally {
    try {
      reader.releaseLock();
    } catch (e) {
      // yoksayılır
    }
  }
}

export default postEventStream;
